import { useState } from 'react';
import { toast } from 'react-toastify';
import {
    agregarHerederoDeclaratoria, editarHerederoDeclaratoria, eliminarHerederoDeclaratoria,
} from '../../services/declaratoriaService';
import { esDniOCuilValido, mensajeDniOCuilInvalido } from '../../utils/documentoUtils';

const VINCULOS = [
    { value: 'CONYUGE', label: 'Cónyuge' },
    { value: 'HIJO', label: 'Hijo/a' },
    { value: 'NIETO', label: 'Nieto/a' },
    { value: 'OTRO', label: 'Otro' },
];

const vacio = () => ({ nombre: '', dni: '', vinculo: 'HIJO', fallecido: false, idHerederoPadre: '' });

function ChipDocumento({ activo, label, onClick, disabled }) {
    return (
        <span
            role="button"
            aria-disabled={disabled}
            onClick={() => !disabled && onClick()}
            className="badge rounded-pill"
            style={{
                cursor: disabled ? 'default' : 'pointer',
                border: `1px solid ${activo ? '#198754' : '#dee2e6'}`,
                background: activo ? '#f0fdf4' : '#f9fafb',
                color: activo ? '#166534' : '#6b7280',
                padding: '0.4rem 0.7rem',
                fontWeight: 600,
            }}
        >
            <i className={`bi ${activo ? 'bi-check-circle-fill' : 'bi-circle'} me-1`}></i>{label}
        </span>
    );
}

/**
 * Árbol de herederos de la Declaratoria: los nietos aparecen sangrados bajo el hijo fallecido del
 * que descienden. Cada fila controla su propia documentación (actas/DNI) con chips que se tildan
 * con un clic — no van al checklist general del trámite.
 */
export default function TablaHerederos({ idTramite, herederos, onActualizado }) {
    const [agregando, setAgregando] = useState(false);
    const [nuevo, setNuevo] = useState(vacio());
    const [editandoId, setEditandoId] = useState(null);
    const [edit, setEdit] = useState(vacio());
    const [guardando, setGuardando] = useState(false);

    const lista = Array.isArray(herederos) ? herederos : [];
    const hijosFallecidos = lista.filter(h => h.vinculo === 'HIJO' && h.fallecido);
    const vivos = lista.filter(h => !h.fallecido);
    const totalDocOk = lista.reduce((acc, h) => {
        const requeridos = 1 + (h.fallecido ? 1 : 0) + (!h.fallecido ? 1 : 0); // acta nac. siempre + (def. o dni)
        const cumplidos = (h.actaNacimientoOk ? 1 : 0) + (h.fallecido ? (h.actaDefuncionOk ? 1 : 0) : (h.dniOk ? 1 : 0));
        return { requeridos: acc.requeridos + requeridos, cumplidos: acc.cumplidos + cumplidos };
    }, { requeridos: 0, cumplidos: 0 });

    const ordenados = [...lista].sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0) || a.id - b.id);

    const toggleCampo = async (heredero, campo) => {
        try {
            await editarHerederoDeclaratoria(idTramite, heredero.id, { [campo]: !heredero[campo] });
            await onActualizado?.();
        } catch (err) {
            console.error('Error al actualizar documentación del heredero:', err);
            toast.error('No se pudo actualizar la documentación');
        }
    };

    const guardarNuevo = async () => {
        if (!nuevo.nombre.trim()) { toast.error('El heredero debe tener un nombre.'); return; }
        if (!esDniOCuilValido(nuevo.dni)) { toast.error(mensajeDniOCuilInvalido('El DNI')); return; }
        setGuardando(true);
        try {
            await agregarHerederoDeclaratoria(idTramite, {
                ...nuevo,
                idHerederoPadre: nuevo.idHerederoPadre === '' ? null : Number(nuevo.idHerederoPadre),
            });
            await onActualizado?.();
            setNuevo(vacio());
            setAgregando(false);
            toast.success('Heredero agregado');
        } catch (err) {
            console.error('Error al agregar heredero:', err);
            toast.error(err.response?.data || 'No se pudo agregar el heredero');
        } finally {
            setGuardando(false);
        }
    };

    const iniciarEdicion = (h) => {
        setEditandoId(h.id);
        setEdit({
            nombre: h.nombre || '', dni: h.dni || '', vinculo: h.vinculo || 'OTRO',
            fallecido: !!h.fallecido, idHerederoPadre: h.idHerederoPadre ?? '',
        });
    };

    const guardarEdicion = async (id) => {
        if (!esDniOCuilValido(edit.dni)) { toast.error(mensajeDniOCuilInvalido('El DNI')); return; }
        setGuardando(true);
        try {
            await editarHerederoDeclaratoria(idTramite, id, {
                ...edit,
                idHerederoPadre: edit.idHerederoPadre === '' ? null : Number(edit.idHerederoPadre),
            });
            await onActualizado?.();
            setEditandoId(null);
            toast.success('Heredero actualizado');
        } catch (err) {
            console.error('Error al editar heredero:', err);
            toast.error(err.response?.data || 'No se pudo editar el heredero');
        } finally {
            setGuardando(false);
        }
    };

    const eliminar = async (h) => {
        if (!window.confirm(`¿Eliminar a ${h.nombre} de la lista de herederos?`)) return;
        try {
            await eliminarHerederoDeclaratoria(idTramite, h.id);
            await onActualizado?.();
            toast.info('Heredero eliminado');
        } catch (err) {
            console.error('Error al eliminar heredero:', err);
            toast.error('No se pudo eliminar el heredero');
        }
    };

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <div className="d-flex gap-2 flex-wrap">
                    <span className="badge rounded-pill bg-secondary-subtle text-secondary-emphasis px-3 py-2">
                        {vivos.length} {vivos.length === 1 ? 'heredero vivo' : 'herederos vivos'}
                    </span>
                    <span className="badge rounded-pill bg-secondary-subtle text-secondary-emphasis px-3 py-2">
                        Documentación {totalDocOk.cumplidos}/{totalDocOk.requeridos}
                    </span>
                </div>
                {!agregando && (
                    <button className="btn btn-sm rounded-pill" style={{ border: '1px solid #9f1239', color: '#9f1239' }}
                        onClick={() => setAgregando(true)}>
                        <i className="bi bi-plus-circle me-1"></i>Agregar heredero
                    </button>
                )}
            </div>

            <div className="d-flex flex-column gap-2">
                {ordenados.map((h) => {
                    const esNieto = h.vinculo === 'NIETO';
                    const enEdicion = editandoId === h.id;
                    return (
                        <div key={h.id} className="border rounded-3 p-3" style={{ marginLeft: esNieto ? '2rem' : 0, background: esNieto ? '#fafafa' : 'white' }}>
                            {!enEdicion ? (
                                <>
                                    <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                                        <div>
                                            <span className="fw-semibold">{h.nombre}</span>
                                            {h.dni && <span className="text-muted ms-2">DNI {h.dni}</span>}
                                            <span className="badge rounded-pill bg-light text-dark border ms-2">
                                                {VINCULOS.find(v => v.value === h.vinculo)?.label || h.vinculo || 'Sin vínculo'}
                                            </span>
                                            {h.fallecido && <span className="badge rounded-pill bg-dark-subtle text-dark-emphasis ms-1">Fallecido</span>}
                                            {h.esCliente && <span className="badge rounded-pill bg-primary-subtle text-primary-emphasis ms-1">Cliente</span>}
                                        </div>
                                        <div className="d-flex gap-1">
                                            <button className="btn btn-sm btn-link p-0 text-secondary" onClick={() => iniciarEdicion(h)} title="Editar">
                                                <i className="bi bi-pencil"></i>
                                            </button>
                                            <button className="btn btn-sm btn-link p-0 text-danger" onClick={() => eliminar(h)} title="Eliminar">
                                                <i className="bi bi-trash"></i>
                                            </button>
                                        </div>
                                    </div>
                                    <div className="d-flex gap-2 flex-wrap mt-2">
                                        <ChipDocumento label="Acta nac." activo={h.actaNacimientoOk} onClick={() => toggleCampo(h, 'actaNacimientoOk')} />
                                        {h.fallecido
                                            ? <ChipDocumento label="Acta def." activo={h.actaDefuncionOk} onClick={() => toggleCampo(h, 'actaDefuncionOk')} />
                                            : <ChipDocumento label="DNI" activo={h.dniOk} onClick={() => toggleCampo(h, 'dniOk')} />}
                                    </div>
                                </>
                            ) : (
                                <div className="d-flex flex-wrap gap-2 align-items-center">
                                    <input className="form-control form-control-sm" style={{ maxWidth: 220 }} value={edit.nombre}
                                        onChange={(e) => setEdit(p => ({ ...p, nombre: e.target.value }))} placeholder="Nombre" />
                                    <input className={`form-control form-control-sm ${!esDniOCuilValido(edit.dni) ? 'is-invalid' : ''}`} style={{ maxWidth: 140 }} value={edit.dni}
                                        onChange={(e) => setEdit(p => ({ ...p, dni: e.target.value }))} placeholder="DNI" />
                                    {!esDniOCuilValido(edit.dni) && (
                                        <div className="text-danger small w-100">DNI inválido: debe tener 7 u 8 dígitos.</div>
                                    )}
                                    <select className="form-select form-select-sm" style={{ maxWidth: 140 }} value={edit.vinculo}
                                        onChange={(e) => setEdit(p => ({ ...p, vinculo: e.target.value }))}>
                                        {VINCULOS.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
                                    </select>
                                    <div className="form-check">
                                        <input className="form-check-input" type="checkbox" id={`fallecido-${h.id}`} checked={edit.fallecido}
                                            onChange={(e) => setEdit(p => ({ ...p, fallecido: e.target.checked }))} />
                                        <label className="form-check-label" htmlFor={`fallecido-${h.id}`}>Falleció</label>
                                    </div>
                                    {edit.vinculo === 'NIETO' && (
                                        <select className="form-select form-select-sm" style={{ maxWidth: 180 }} value={edit.idHerederoPadre}
                                            onChange={(e) => setEdit(p => ({ ...p, idHerederoPadre: e.target.value }))}>
                                            <option value="">Hijo del que desciende...</option>
                                            {hijosFallecidos.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                                        </select>
                                    )}
                                    <button className="btn btn-sm btn-primary rounded-pill" disabled={guardando} onClick={() => guardarEdicion(h.id)}>
                                        Guardar
                                    </button>
                                    <button className="btn btn-sm btn-outline-secondary rounded-pill" onClick={() => setEditandoId(null)}>
                                        Cancelar
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {agregando && (
                <div className="border rounded-3 p-3 mt-3 bg-light">
                    <div className="d-flex flex-wrap gap-2 align-items-center">
                        <input className="form-control form-control-sm" style={{ maxWidth: 220 }} value={nuevo.nombre}
                            onChange={(e) => setNuevo(p => ({ ...p, nombre: e.target.value }))} placeholder="Nombre" />
                        <input className={`form-control form-control-sm ${!esDniOCuilValido(nuevo.dni) ? 'is-invalid' : ''}`} style={{ maxWidth: 140 }} value={nuevo.dni}
                            onChange={(e) => setNuevo(p => ({ ...p, dni: e.target.value }))} placeholder="DNI" />
                        {!esDniOCuilValido(nuevo.dni) && (
                            <div className="text-danger small w-100">DNI inválido: debe tener 7 u 8 dígitos.</div>
                        )}
                        <select className="form-select form-select-sm" style={{ maxWidth: 140 }} value={nuevo.vinculo}
                            onChange={(e) => setNuevo(p => ({ ...p, vinculo: e.target.value }))}>
                            {VINCULOS.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
                        </select>
                        <div className="form-check">
                            <input className="form-check-input" type="checkbox" id="nuevo-fallecido" checked={nuevo.fallecido}
                                onChange={(e) => setNuevo(p => ({ ...p, fallecido: e.target.checked }))} />
                            <label className="form-check-label" htmlFor="nuevo-fallecido">Falleció</label>
                        </div>
                        {nuevo.vinculo === 'NIETO' && (
                            <select className="form-select form-select-sm" style={{ maxWidth: 180 }} value={nuevo.idHerederoPadre}
                                onChange={(e) => setNuevo(p => ({ ...p, idHerederoPadre: e.target.value }))}>
                                <option value="">Hijo del que desciende...</option>
                                {hijosFallecidos.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                            </select>
                        )}
                        <button className="btn btn-sm btn-primary rounded-pill" disabled={guardando} onClick={guardarNuevo}>
                            {guardando ? <span className="spinner-border spinner-border-sm" role="status"></span> : 'Agregar'}
                        </button>
                        <button className="btn btn-sm btn-outline-secondary rounded-pill" onClick={() => { setAgregando(false); setNuevo(vacio()); }}>
                            Cancelar
                        </button>
                    </div>
                </div>
            )}

            {lista.length === 0 && !agregando && (
                <p className="text-muted fst-italic mt-2 mb-0">No hay herederos cargados todavía.</p>
            )}
        </div>
    );
}
